import {
  CommandPalette,
  CommandPaletteTrigger,
  Icons,
  useCommandPalette,
  type CommandPaletteItem,
  type LucideIcon,
} from '@munifocus/ui';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { Permission } from '@munifocus/access';
import type { FileDetail } from '@munifocus/contracts-gov';
import { useOptionalScope } from '../providers/ScopeProvider';
import { globalSearch } from '../lib/search-api';
import { filesKeys } from '../lib/files-api';
import { readRecentFiles } from '../lib/recent-files';
import { fileIdFromPath, useKeyboardShortcuts, type ShortcutHandlers } from '../lib/shortcuts';
import { ShortcutHelpDialog } from './ShortcutHelpDialog';

/**
 * Groups, in render order. Actions on the open file come first — they are why
 * an officer reaches for the palette from inside a file — then the static
 * commands, then the files they opened lately, then remote hits.
 */
const GROUP_ORDER = ['fileActions', 'commands', 'recent', 'file'];

/** Static destinations, keyed by their i18n leaf under `gov.commandPalette`. */
const COMMANDS: { key: string; href?: string; icon: LucideIcon; needs?: Permission }[] = [
  { key: 'dashboard', href: '/', icon: Icons.LayoutDashboard },
  // Starting a file needs an appointment. Offering it to a contractor's
  // unappointed employee sends them to a form the API will refuse.
  { key: 'newFile', href: '/files/new', icon: Icons.Plus, needs: 'file:create' },
  { key: 'capture', href: '/capture', icon: Icons.Camera },
  { key: 'map', href: '/map', icon: Icons.Map },
  { key: 'docket', href: '/docket', icon: Icons.Gavel },
  { key: 'settings', href: '/settings', icon: Icons.SlidersHorizontal },
  // Not a destination: opens the shortcut overlay. Handled in `onSelect`.
  { key: 'shortcuts', icon: Icons.Keyboard },
];

/**
 * What can be done to the file that is open, as palette actions. Each lands on
 * the place the work is done: the site-visit record, or the file page scrolled
 * to the section (`?focus=`), where the officer finishes it.
 */
const FILE_ACTIONS: {
  key: string;
  path: (id: string) => string;
  icon: LucideIcon;
  needs?: Permission;
}[] = [
  {
    key: 'logVisit',
    path: (id) => `/files/${id}/stage/site_visit`,
    icon: Icons.MapPin,
    needs: 'file:update',
  },
  {
    key: 'generateDocument',
    path: (id) => `/files/${id}?focus=documents`,
    icon: Icons.FileSignature,
  },
  {
    key: 'assign',
    path: (id) => `/files/${id}?focus=team`,
    icon: Icons.UserPlus,
  },
  {
    key: 'viewVisits',
    path: (id) => `/files/${id}?focus=visits`,
    icon: Icons.Route,
  },
  { key: 'edit', path: (id) => `/files/${id}/edit`, icon: Icons.Pencil, needs: 'file:update' },
];

/** Where each file shortcut goes — the same destinations as the palette actions. */
const FILE_SHORTCUT_ACTION: Record<string, string> = {
  fileVisit: 'logVisit',
  fileDocuments: 'generateDocument',
  fileAssign: 'assign',
  fileEdit: 'edit',
};

const RESULT_ICONS: Record<string, LucideIcon> = { file: Icons.FileText };

const SHORTCUTS_ID = 'command:shortcuts';

/**
 * The Government Portal's Cmd-K palette plus its header affordance, and the
 * desk portal's keyboard shortcuts.
 *
 * Renders both as siblings so they share one piece of open state; the dialog
 * portals to the document body, so its position in the header markup does not
 * constrain its layout.
 */
export function GlobalSearch({ expandAt = 'md' }: { expandAt?: 'md' | 'lg' } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { open, openPalette, closePalette, shortcutLabel } = useCommandPalette();
  const [helpOpen, setHelpOpen] = useState(false);

  // Optional: the palette is chrome and renders inside component tests that
  // have no ScopeProvider above them. With no scope there is nothing to gate
  // on, so every command shows — the route guard still refuses.
  const scope = useOptionalScope();
  const allowed = useCallback(
    (needs?: Permission) => !needs || !scope || scope.can(needs),
    [scope],
  );

  const fileId = fileIdFromPath(location.pathname);
  // The open file's number, from the cache the file page already filled. A
  // palette opened before it loaded falls back to a generic label.
  const openFile = fileId
    ? queryClient.getQueryData<FileDetail>(filesKeys.detail(fileId))
    : undefined;

  const commands = useMemo<CommandPaletteItem[]>(() => {
    const items: CommandPaletteItem[] = [];
    if (fileId) {
      for (const action of FILE_ACTIONS) {
        if (!allowed(action.needs)) continue;
        items.push({
          id: `file-action:${action.key}`,
          group: 'fileActions',
          title: t(`gov.commandPalette.fileActions.${action.key}`),
          subtitle: openFile ? `${openFile.reference} · ${openFile.address}` : undefined,
          href: action.path(fileId),
          icon: action.icon,
          keywords: t(`gov.commandPalette.fileActionKeywords.${action.key}`),
        });
      }
    }
    for (const command of COMMANDS) {
      if (!allowed(command.needs)) continue;
      items.push({
        id: `command:${command.key}`,
        group: 'commands',
        title: t(`gov.commandPalette.commands.${command.key}`),
        href: command.href,
        icon: command.icon,
        // Hidden synonyms, so an officer working in French can still find
        // "Paramètres" by typing "settings" (and vice versa).
        keywords: t(`gov.commandPalette.keywords.${command.key}`),
      });
    }
    // Read on each open: another tab may have opened files since.
    if (open) {
      for (const recent of readRecentFiles()) {
        if (recent.id === fileId) continue;
        items.push({
          id: `recent:${recent.id}`,
          group: 'recent',
          title: recent.reference,
          subtitle: recent.address,
          href: `/files/${recent.id}`,
          icon: Icons.History,
        });
      }
    }
    return items;
    // `open` re-reads the recents each time the palette is shown.
  }, [t, allowed, fileId, openFile, open]);

  const search = useCallback(
    async (query: string, signal: AbortSignal): Promise<CommandPaletteItem[]> => {
      const results = await globalSearch(query, signal);
      return results.map((result) => ({
        id: `${result.type}:${result.id}`,
        group: result.type,
        title: result.title,
        subtitle: result.subtitle,
        href: result.href,
        icon: RESULT_ICONS[result.type],
      }));
    },
    [],
  );

  const groupLabels = useMemo(
    () => ({
      fileActions: t('gov.commandPalette.groups.fileActions'),
      commands: t('common.commandPalette.groups.commands'),
      recent: t('gov.commandPalette.groups.recent'),
      file: t('gov.commandPalette.groups.file'),
    }),
    [t],
  );

  const labels = useMemo(
    () => ({
      title: t('common.commandPalette.title'),
      placeholder: t('gov.commandPalette.placeholder'),
      empty: t('common.commandPalette.empty'),
      loading: t('common.commandPalette.loading'),
      noResults: t('common.commandPalette.noResults'),
      resultCountLabel: (count: number) => t('common.commandPalette.resultCount', { count }),
      hintNavigate: t('common.commandPalette.hintNavigate'),
      hintSelect: t('common.commandPalette.hintSelect'),
      hintClose: t('common.commandPalette.hintClose'),
    }),
    [t],
  );

  const onSelect = useCallback(
    (item: CommandPaletteItem) => {
      if (item.id === SHORTCUTS_ID) {
        setHelpOpen(true);
        return;
      }
      if (item.href) navigate(item.href);
    },
    [navigate],
  );

  const shortcutHandlers = useMemo<ShortcutHandlers>(() => {
    const handlers: ShortcutHandlers = {
      help: () => setHelpOpen(true),
      palette: openPalette,
      goDashboard: () => navigate('/'),
      goMap: () => navigate('/map'),
    };
    if (allowed('file:create')) handlers.newFile = () => navigate('/files/new');
    if (fileId) {
      for (const [shortcut, actionKey] of Object.entries(FILE_SHORTCUT_ACTION)) {
        const action = FILE_ACTIONS.find((a) => a.key === actionKey);
        if (!action || !allowed(action.needs)) continue;
        handlers[shortcut as keyof ShortcutHandlers] = () => navigate(action.path(fileId));
      }
    }
    return handlers;
  }, [allowed, fileId, navigate, openPalette]);

  // Off while either overlay is up: they own the keyboard.
  useKeyboardShortcuts(shortcutHandlers, !open && !helpOpen);

  return (
    <>
      <CommandPaletteTrigger
        onClick={openPalette}
        label={t('common.commandPalette.trigger')}
        shortcutLabel={shortcutLabel}
        // The mobile header carries the MuniFocus lockup right up to `md`, where
        // the desktop rail takes over. Expanding at `sm` put a 200px field beside
        // it on tablets and phablets, and the brand — the only flexible item in
        // that row — was what gave way.
        // A page with top-bar actions keeps the icon form up to `lg`: between
        // md and lg the full field plus the actions left the title no room
        // and pushed the avatar off the edge.
        expandAt={expandAt}
      />
      <CommandPalette
        open={open}
        onClose={closePalette}
        commands={commands}
        search={search}
        onSelect={onSelect}
        groupLabels={groupLabels}
        groupOrder={GROUP_ORDER}
        labels={labels}
      />
      <ShortcutHelpDialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        paletteShortcut={shortcutLabel}
      />
    </>
  );
}
